import type {
  ChangeOnboardingContentSkipCountRequest,
  ChangeOnboardingProgressData,
  ChangeOnboardingProgressRequest,
  GetOnboardingContentsData,
  GetRedirectUrlData,
  GetRedirectUrlParams,
  GetTargetVisibilityData,
  UpsertSkipCountData,
} from '~/api-spec/__generated__/main'
import { httpClient, type RequestMeta } from '~/http/client'

import {
  adaptOnboardingContents,
  adaptOnboardingVisibility,
  adaptOnlineOnboardingRedirect,
} from './adapters'
import type {
  OnboardingContent,
  OnboardingContents,
  OnboardingVisibility,
  OnlineOnboardingRedirect,
} from './schemas'

// 온보딩 컨트롤러는 CommonResponse 봉투 없이 본문을 바로 낸다. 두 변경 요청의 응답 본문은 쓰지 않는다.
// 2026-10-14 백엔드 develop(6a0f8e7bb) 의 onboarding/controller/OnboardingController.kt·request/ 로 확인.

export type OnboardingStatusBody = ChangeOnboardingProgressRequest
/** 서버가 0~4 로 검증하고, 3 이상이면 진행 상태를 SKIPPED 로 바꾼다 */
export type OnboardingSkipCountBody = ChangeOnboardingContentSkipCountRequest

export const getOnboardingContents = async (meta?: RequestMeta): Promise<OnboardingContents> => {
  const body = await httpClient().get<GetOnboardingContentsData>('/onboardings/contents', { meta })
  return adaptOnboardingContents(body)
}

/** 요금제와 최근 30일 활동으로 정한 안내 툴팁 노출 여부. 정식 가입자 대상 세 콘텐츠만 true 가 될 수 있다 */
export const getOnboardingVisibility = async (
  id: OnboardingContent['id'],
  meta?: RequestMeta,
): Promise<OnboardingVisibility> => {
  const body = await httpClient().get<GetTargetVisibilityData>(`/onboardings/${id}/visibility`, {
    meta,
  })
  return adaptOnboardingVisibility(body)
}

export const updateOnboardingStatus = async (
  id: OnboardingContent['id'],
  body: OnboardingStatusBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().post<ChangeOnboardingProgressData>(
    `/onboardings/progresses/${id}/status`,
    body,
    { meta },
  )
}

export const updateOnboardingSkipCount = async (
  id: OnboardingContent['id'],
  body: OnboardingSkipCountBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().post<UpsertSkipCountData>(`/onboardings/progresses/${id}/skip-count`, body, {
    meta,
  })
}

/** 온라인 온보딩 랜딩 주소. 돌아갈 주소(redirectUrl)를 빼면 서버가 기본 랜딩을 채운다 */
export const getOnlineOnboardingRedirect = async (
  redirectUrl?: GetRedirectUrlParams['redirectUrl'] | null,
  meta?: RequestMeta,
): Promise<OnlineOnboardingRedirect> =>
  adaptOnlineOnboardingRedirect(
    await httpClient().get<GetRedirectUrlData>('/sso/qubemaker/redirect-url', {
      params: { redirectUrl },
      meta,
    }),
  )
